import { useRaffleMutations } from '@/app/hooks/useRaffleMutations'
import { useRaffleById } from '@/app/hooks/useRaffles'
import ButtonBack from '@/view/components/ButtonBack'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/view/components/ui/alert-dialog'
import { Button } from '@/view/components/ui/button'
import { Separator } from '@/view/components/ui/separator'
import { Spinner } from '@/view/components/ui/spinner'
import { format } from 'date-fns'
import { Calendar, Pencil, Trash } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { DrawRaffle } from './components/DrawRaffle'
import ManageRafflePrizes from './components/ManageRafflePrizes'
import { RaffleWinners } from './components/RaffleWinners'
import { raffleHasEnded } from './utils'
import { useAuth } from '@/app/hooks/useAuth'

export const ViewRaffleDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: raffle, isLoading } = useRaffleById(id!)
  const { deleteRaffle } = useRaffleMutations()

  const { PERMISSIONS } = useAuth()

  const handleBack = () => {
    navigate('/sorteios/gerenciar')
  }

  const handleEdit = () => {
    navigate(`/sorteios/gerenciar/${id}`)
  }

  const handleDelete = async () => {
    try {
      await deleteRaffle.mutateAsync(id!)
      navigate('/sorteios/gerenciar')
    } catch (error) {
      console.error(error)
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (!raffle) {
    return <div>Sorteio não encontrado</div>
  }

  const hasEnded = raffleHasEnded(raffle)

  return (
    <div className="flex w-full flex-col">
      <ButtonBack label={raffle.name} action={handleBack} />

      <div className="flex flex-col gap-4 rounded-md border p-4 sm:flex-row sm:items-start sm:gap-6 sm:p-5 lg:gap-8">
        <img
          src={`${import.meta.env.VITE_CDN_URL}/${raffle.banner}`}
          alt={raffle.name}
          className="h-[200px] w-full rounded-lg object-cover sm:h-[123px] sm:w-auto sm:flex-shrink-0"
          style={{ aspectRatio: '1179 / 1600' }}
        />

        <div className="flex flex-1 flex-col gap-4 sm:gap-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-between">
            <div className="flex flex-col">
              <h2 className="text-lg font-semibold sm:text-xl">
                {raffle.name}
              </h2>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-5">
                {hasEnded ? (
                  <span className="text-[#9F9F9F]">Finalizado</span>
                ) : (
                  <span className="text-blue-500">Em andamento</span>
                )}
                <Separator orientation="vertical" className="hidden sm:block" />
                <span className="font-semibold">
                  {raffle.couponsCount ?? 0} Cupons válidos
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:gap-3 lg:gap-5">
              {PERMISSIONS.raffles.execute && (
                <DrawRaffle
                  raffleId={raffle._id}
                  hasCoupons={raffle?.couponsCount > 0}
                />
              )}

              {PERMISSIONS.raffles.edit && (
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleEdit}
                  className="w-full sm:w-auto"
                >
                  <Pencil size={20} className="mr-2" />
                  Editar
                </Button>
              )}

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="w-full sm:w-auto"
                  >
                    <Trash size={20} />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Excluir sorteio</AlertDialogTitle>
                    <AlertDialogDescription>
                      Tem certeza que deseja excluir este sorteio? Esta ação não
                      pode ser desfeita.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete}>
                      Confirmar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-4 flex flex-col rounded-md border p-4 sm:mt-5 sm:p-5">
        <div className="flex flex-col gap-4 sm:gap-5">
          <h3 className="text-base font-semibold sm:text-lg">
            Data do sorteio
          </h3>
          <div className="flex items-center gap-3 sm:gap-5">
            <div className="flex items-center gap-2">
              <Calendar size={18} className="sm:hidden" />
              <Calendar size={20} className="hidden sm:block" />
              <span className="text-base font-medium sm:text-lg">
                {format(raffle?.resultDate ?? new Date(), 'dd.MM.yyyy')}
              </span>
            </div>
          </div>
          <Separator />
          <ManageRafflePrizes
            prizes={raffle?.prizes ?? []}
            raffleId={raffle?._id}
          />
          <RaffleWinners winners={raffle?.winners ?? []} />
        </div>
      </div>
    </div>
  )
}
